"use client"

import type React from "react"
import { useEffect, useState, useRef } from "react"
import { useParams } from "react-router-dom"
import { getToken, getAssetUrl, getRole } from "../utils/auth.ts"
import { isEmployeePortalRole } from "../utils/authRedirects.ts"
import { portalHomePath } from "../utils/employeePortalNavigation.ts"
import { useCompany } from "../context/CompanyContext.tsx"
import { useReactToPrint } from "react-to-print"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import axiosInstance from "../utils/axiosConfig.ts"
import { Sidebar } from "../components/sideNavbar.tsx"
import { Topbar } from "../components/topNavbar.tsx"
import { useOptionalAdminChrome } from "../context/AdminLayoutContext.tsx"
import { InvoicePaymentMethodCard } from "../components/invoice/InvoicePaymentMethodCard.tsx"
import { InvoiceLineItems } from "../components/invoice/InvoiceLineItems.tsx"
import { InvoiceReferralDiscounts } from "../components/invoice/InvoiceReferralDiscounts.tsx"
import type { ReferralDiscountRow } from "../components/invoice/referralDiscountUtils.ts"
import { InvoicePaymentSummary } from "../components/invoice/InvoicePaymentSummary.tsx"
import { InvoiceStatusBadge } from "../components/invoice/InvoiceStatusBadge.tsx"
import {
  formatInvoiceDate,
  getPaymentStatusColor,
  isSubscriptionInvoice,
} from "../components/invoice/invoiceUtils.ts"
import { Link } from "react-router-dom"
import MBALogo from "../assets/mba_logo.tsx"
import { PaidStamp } from "../components/PaidStamp.tsx"

interface LineItem {
  id: string
  description: string
  quantity: number
  unit_price: number
  discount_amount: number
  line_total: number
  charge_type?: string
  item_type?: string
  inventory_item_id?: string
  inventory_item_type?: string
  billing_start_date?: string | null
  billing_end_date?: string | null
}

interface PendingInvoice {
  id: string
  invoice_number: string
  billing_start_date: string
  billing_end_date: string
  due_date: string
  total_amount: number
  paid_amount: number
  remaining_amount: number
  status: string
  invoice_type: string
}

interface PendingInvoicesData {
  count: number
  total_pending_amount: number
  invoices: PendingInvoice[]
}

interface InvoiceData {
  id: string
  invoice_number: string
  customer_name: string
  customer_address: string
  customer_internet_id: string
  customer_phone: string
  service_plan_name: string
  billing_start_date: string
  billing_end_date: string
  due_date: string
  subtotal: number
  discount_percentage: number
  total_amount: number
  pre_referral_total?: number
  referral_discount_total?: number
  referral_discounts?: ReferralDiscountRow[]
  invoice_type: string
  notes: string
  status: string
  line_items: LineItem[]
  payments: PaymentDetails[]
  total_paid: number
  remaining_amount: number
  pending_invoices?: PendingInvoicesData
  company?: {
    name?: string
    address?: string
    contact_number?: string
    email?: string
    tagline?: string
    tax_number?: string
    invoice_footer_notes?: string
    logo_url?: string | null
  }
}

interface PaymentDetails {
  id: string
  amount: number
  payment_date: string
  payment_method: string
  transaction_id?: string
  status: string
  failure_reason?: string
}

interface BankAccount {
  id: string
  bank_name: string
  account_title: string
  account_number: string
  iban?: string
  branch_code?: string
  qr_code_image?: string | null
}

const InvoiceGenerationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [invoiceData, setInvoiceData] = useState<InvoiceData | null>(null)
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([])
  const [isPrinting, setIsPrinting] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const printRef = useRef<HTMLDivElement>(null)
  const hasChrome = useOptionalAdminChrome()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  const { setPageTitle } = useCompany()

  useEffect(() => {
    setPageTitle("Invoice Details")
    fetchInvoiceData()
    fetchBankAccounts()
  }, [id, setPageTitle])

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev)

  const fetchInvoiceData = async () => {
    try {
      const token = getToken()
      const response = await axiosInstance.get(`/invoices/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      setInvoiceData(response.data)
      setError(null)
    } catch (error) {
      console.error("Failed to fetch invoice data", error)
      setError("Failed to load invoice data. Please try again.")
    }
  }

  const fetchBankAccounts = async () => {
    try {
      const token = getToken()
      const response = await axiosInstance.get("/bank-accounts/list?active_only=true", { headers: { Authorization: `Bearer ${token}` } })
      setBankAccounts(response.data)
    } catch (error) {
      console.error("Failed to fetch bank accounts", error)
    }
  }

  const formatDate = formatInvoiceDate

  const isSubscription = isSubscriptionInvoice(invoiceData?.invoice_type, invoiceData?.line_items)
  const invoiceDateToShow = isSubscription ? invoiceData?.billing_start_date : invoiceData?.due_date || invoiceData?.billing_start_date

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Invoice-${invoiceData?.invoice_number}`,
    onBeforeGetContent: () => { setIsPrinting(true); return new Promise((resolve) => { setTimeout(resolve, 100) }) },
    onAfterPrint: () => setIsPrinting(false),
    removeAfterPrint: true,
    onPrintError: (error: any) => { console.error("Print failed:", error); setError("Failed to print. Please try again."); setIsPrinting(false) },
  } as any)

  const printInvoice = () => { try { handlePrint() } catch (error) { console.error("Error during print:", error); setError("An unexpected error occurred while printing. Please try again."); setIsPrinting(false) } }

  const handleDownloadPDF = async () => {
    if (!printRef.current) return
    setIsDownloading(true)
    setError(null)
    try {
      const canvas = await html2canvas(printRef.current, { scale: 2, useCORS: true, allowTaint: true, backgroundColor: "#ffffff", width: printRef.current.scrollWidth, height: printRef.current.scrollHeight })
      const imgData = canvas.toDataURL("image/jpeg", 0.8)
      const pdf = new jsPDF("p", "mm", "a4")
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = pdfWidth
      const imgHeight = (canvas.height * pdfWidth) / canvas.width
      let heightLeft = imgHeight
      let position = 0
      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight)
      heightLeft -= pdfHeight
      while (heightLeft >= 0) { position = heightLeft - imgHeight; pdf.addPage(); pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight); heightLeft -= pdfHeight }
      pdf.save(`Invoice-${invoiceData?.invoice_number}.pdf`)
    } catch (error) { console.error("PDF generation failed:", error); setError("Failed to generate PDF. Please try again.") } finally { setIsDownloading(false) }
  }

  const handleShareWhatsApp = async () => {
    if (!invoiceData?.customer_phone || !printRef.current) return
    try {
      setError(null)
      let phoneNumber = invoiceData.customer_phone.replace(/\D/g, "")
      if (phoneNumber.startsWith("92") && phoneNumber.length === 12) { }
      else if (phoneNumber.startsWith("03") && phoneNumber.length === 11) { phoneNumber = "92" + phoneNumber.substring(1) }
      else if (phoneNumber.startsWith("3") && phoneNumber.length === 10) { phoneNumber = "92" + phoneNumber }
      else if (phoneNumber.startsWith("0092") && phoneNumber.length === 14) { phoneNumber = phoneNumber.substring(2) }
      else if (phoneNumber.startsWith("+92") && phoneNumber.length === 13) { phoneNumber = phoneNumber.substring(1) }
      const publicInvoiceUrl = `${window.location.origin}/public/invoice/${invoiceData.id}`
      const compName = invoiceData?.company?.name || 'our service'
      const message = `Hello ${invoiceData.customer_name},\n\nYour invoice #${invoiceData.invoice_number} is ready.\n\n📋 Invoice Details:\n• Amount: PKR ${invoiceData.total_amount.toFixed(2)}\n• Due Date: ${formatDate(invoiceData.due_date)}\n• Status: ${invoiceData.status}\n\n📄 View your complete invoice here:\n${publicInvoiceUrl}\n\nPlease review your invoice and make the payment if pending.\n\nThank you for choosing ${compName}!`
      const whatsappAppUrl = `whatsapp://send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`
      const whatsappWebUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`
      window.location.href = whatsappAppUrl
      setTimeout(() => { window.open(whatsappWebUrl, "_blank") }, 1000)
    } catch (error) { console.error("WhatsApp share failed:", error); setError("Failed to share via WhatsApp. Please try again.") }
  }

  if (!invoiceData && !error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-light-sky/50">
        <div className="text-center">
          <div className="relative mx-auto mb-4 h-10 w-10">
            <div className="absolute inset-0 animate-spin rounded-full border-2 border-electric-blue border-t-transparent" />
          </div>
          <p className="text-sm font-medium text-slate-gray">Loading invoice...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-light-sky/50">
        <div className="max-w-md rounded-xl border border-red-100 bg-white p-6 text-center shadow-sm">
          <h2 className="mb-2 text-lg font-semibold text-slate-800">Error Loading Invoice</h2>
          <p className="mb-4 text-sm text-slate-600">{error}</p>
          <button onClick={fetchInvoiceData} className="h-9 rounded-lg bg-electric-blue px-4 text-sm font-medium text-white hover:bg-btn-hover">Try Again</button>
        </div>
      </div>
    )
  }

  const employeeView = isEmployeePortalRole(getRole())
  const useAdminShell = !employeeView && !hasChrome
  const canShareWhatsApp = Boolean(invoiceData?.customer_phone)

  return (
    <div className={hasChrome && !employeeView ? "flex-1 min-w-0 w-full" : employeeView ? "min-h-screen bg-light-sky/50" : "flex h-screen bg-light-sky/50"}>
      {useAdminShell && (
        <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} setIsOpen={setIsSidebarOpen} />
      )}
      <div className={hasChrome && !employeeView ? "flex-1 min-w-0 w-full" : "flex-1 flex flex-col overflow-hidden"}>
        {useAdminShell && <Topbar toggleSidebar={toggleSidebar} />}
        <div className={
          employeeView
            ? "flex-1 overflow-x-hidden overflow-y-auto px-4 sm:px-6 pb-4 sm:pb-6 pt-4"
            : hasChrome
              ? "px-4 sm:px-6 py-4 sm:py-6"
              : `flex-1 overflow-x-hidden overflow-y-auto px-4 sm:px-6 pb-4 sm:pb-6 pt-20 transition-all duration-300 ${isSidebarOpen ? "lg:ml-72" : "ml-0 lg:ml-20"}`
        }>
          <div className="max-w-4xl mx-auto">
            {employeeView && (
              <div className="mb-4">
                <Link
                  to={portalHomePath()}
                  className="inline-flex items-center gap-1.5 text-sm text-[#6B8A94] hover:text-[#89A8B2]"
                >
                  ← Back to portal
                </Link>
              </div>
            )}
            {/* Top Action Bar */}
            <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-lg font-semibold text-deep-ocean sm:text-xl">
                  Invoice #{invoiceData?.invoice_number}
                </h1>
                <p className="mt-0.5 text-xs text-slate-gray sm:text-sm">
                  Due {invoiceData?.due_date && formatDate(invoiceData.due_date)}
                  {(invoiceData?.remaining_amount || 0) > 0 && (
                    <> · PKR {invoiceData?.remaining_amount.toLocaleString()} due</>
                  )}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={printInvoice} disabled={isPrinting}
                  className={`flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:border-electric-blue hover:text-electric-blue ${isPrinting ? "cursor-not-allowed opacity-50" : ""}`}>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                  {isPrinting ? "Printing..." : "Print"}
                </button>
                <button onClick={handleDownloadPDF} disabled={isDownloading}
                  className={`flex h-9 items-center gap-1.5 rounded-lg bg-electric-blue px-3 text-sm font-medium text-white hover:bg-btn-hover ${isDownloading ? "cursor-not-allowed opacity-50" : ""}`}>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  {isDownloading ? "Generating..." : "Download PDF"}
                </button>
                <button onClick={handleShareWhatsApp} disabled={!canShareWhatsApp}
                  title={canShareWhatsApp ? "Share via WhatsApp" : "Customer phone not available"}
                  className={`flex h-9 items-center gap-1.5 rounded-lg bg-[#25D366] px-3 text-sm font-medium text-white hover:bg-[#128C7E] ${!canShareWhatsApp ? "cursor-not-allowed opacity-50" : ""}`}>
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z" /></svg>
                  WhatsApp
                </button>
              </div>
            </div>

            {/* Main Invoice Card */}
            <div className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
              <div ref={printRef} className="p-4 sm:p-8">

                {/* Header Section */}
                <div className="flex flex-col gap-5 border-b border-slate-100 pb-6 sm:grid sm:grid-cols-[minmax(0,1fr)_8rem_minmax(12rem,auto)] sm:items-start sm:gap-6 sm:pb-8">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                      <span className="text-xl font-bold text-deep-ocean sm:text-2xl">INVOICE</span>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <InvoiceStatusBadge status={invoiceData?.status || ""} />
                        {invoiceData?.invoice_type && (
                          <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                            {invoiceData.invoice_type.split('_').join(' ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="mt-1 font-mono text-sm text-slate-500">#{invoiceData?.invoice_number}</p>
                  </div>
                  <div className="flex min-h-[6.5rem] items-center justify-start sm:justify-center">
                    {invoiceData?.status === 'paid' && (
                      <PaidStamp
                        date={invoiceData.payments?.[invoiceData.payments.length - 1]?.payment_date}
                        method={invoiceData.payments?.[invoiceData.payments.length - 1]?.payment_method}
                        className="h-20 w-20 text-emerald-600 border-emerald-600 sm:h-28 sm:w-28"
                      />
                    )}
                  </div>
                  <div className="flex w-full min-w-0 flex-col items-start text-left sm:w-auto sm:items-end sm:text-right">
                    <div className="mb-2 inline-flex h-10 items-center justify-start gap-2 sm:mb-3 sm:h-12 sm:justify-end sm:gap-3">
                      {invoiceData?.company?.logo_url ? (
                        <img
                          src={getAssetUrl(invoiceData.company.logo_url)!}
                          alt={invoiceData.company.name}
                          className="max-h-10 max-w-[120px] object-contain sm:max-h-12 sm:max-w-[140px]"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <div className="flex h-8 w-24 items-center sm:h-10 sm:w-28">
                          <MBALogo variant="landscape" companyName={invoiceData?.company?.name} />
                        </div>
                      )}
                    </div>
                    <p className="font-bold text-[#2A5C8A] text-base">{invoiceData?.company?.name || ""}</p>
                    {invoiceData?.company?.address && (
                      <p className="text-slate-500 text-xs leading-relaxed whitespace-pre-line">
                        {invoiceData.company.address}
                      </p>
                    )}
                    {invoiceData?.company?.contact_number && (
                      <p className="text-slate-500 text-xs">{invoiceData.company.contact_number}</p>
                    )}
                    {invoiceData?.company?.tax_number && (
                      <p className="text-slate-500 text-xs font-mono">Tax Reg: {invoiceData.company.tax_number}</p>
                    )}
                  </div>
                </div>

                {/* Customer & Invoice Meta Grid */}
                <div className="grid gap-4 border-b border-slate-100 py-6 sm:gap-6 sm:py-8 md:grid-cols-2">
                  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 sm:p-5">
                    <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Bill To</p>
                    <p className="mb-1 text-base font-bold text-slate-800 sm:text-lg">{invoiceData?.customer_name}</p>
                    <div className="space-y-1 text-sm text-slate-600">
                      <p><span className="text-slate-400">ID:</span> {invoiceData?.customer_internet_id}</p>
                      <p><span className="text-slate-400">Phone:</span> {invoiceData?.customer_phone}</p>
                      <p className="text-slate-500">{invoiceData?.customer_address}</p>
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 sm:p-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Issue Date</p>
                        <p className="text-sm font-semibold text-slate-700">{invoiceDateToShow && formatDate(invoiceDateToShow)}</p>
                      </div>
                      <div>
                        <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Due Date</p>
                        <p className="text-sm font-semibold text-slate-700">{invoiceData?.due_date && formatDate(invoiceData.due_date)}</p>
                      </div>
                    </div>
                    {isSubscription && (
                      <div className="mt-4 border-t border-slate-200/50 pt-4">
                        <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Billing Period</p>
                        <p className="text-sm font-semibold text-slate-700">{invoiceData?.billing_start_date && formatDate(invoiceData.billing_start_date)} — {invoiceData?.billing_end_date && formatDate(invoiceData.billing_end_date)}</p>
                      </div>
                    )}
                  </div>
                </div>

                <InvoiceLineItems
                  lineItems={invoiceData?.line_items}
                  invoiceType={invoiceData?.invoice_type}
                  servicePlanName={invoiceData?.service_plan_name || ""}
                  subtotal={invoiceData?.subtotal ?? 0}
                  discountPercentage={invoiceData?.discount_percentage ?? 0}
                  totalAmount={invoiceData?.pre_referral_total ?? invoiceData?.total_amount ?? 0}
                  totalPaid={invoiceData?.total_paid ?? 0}
                  remainingAmount={invoiceData?.remaining_amount ?? 0}
                  isSubscription={isSubscription}
                  billingStartDate={invoiceData?.billing_start_date}
                  billingEndDate={invoiceData?.billing_end_date}
                  issueDate={invoiceDateToShow}
                />

                <InvoiceReferralDiscounts
                  rows={invoiceData?.referral_discounts}
                  referralDiscountTotal={invoiceData?.referral_discount_total ?? 0}
                  preReferralTotal={invoiceData?.pre_referral_total}
                />

                <InvoicePaymentSummary
                  totalAmount={invoiceData?.total_amount ?? 0}
                  totalPaid={invoiceData?.total_paid ?? 0}
                  remainingAmount={invoiceData?.remaining_amount ?? 0}
                />

                {/* Payment History */}
                {invoiceData?.payments && invoiceData.payments.length > 0 && (
                  <div className="border-b border-slate-100 py-6 sm:py-8">
                    <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Payment History</p>
                    <div className="space-y-3">
                      {invoiceData.payments.map((payment, index) => (
                        <div key={payment.id} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-800">Payment #{invoiceData.payments.length - index}</p>
                              <p className="text-xs text-slate-500">{formatDate(payment.payment_date)} • {payment.payment_method}</p>
                              {payment.transaction_id && <p className="mt-1 truncate text-xs text-slate-400">Ref: {payment.transaction_id}</p>}
                            </div>
                            <div className="flex items-center justify-between gap-3 sm:flex-col sm:items-end">
                              <p className="text-base font-bold tabular-nums text-slate-800 sm:text-lg">PKR {payment.amount.toLocaleString()}</p>
                              <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${getPaymentStatusColor(payment.status)}`}>{payment.status}</span>
                            </div>
                          </div>
                          {payment.failure_reason && payment.status === 'failed' && (
                            <p className="mt-2 rounded-lg border border-red-100 bg-red-50 p-2 text-xs text-red-600">{payment.failure_reason}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending Payment Alert */}
                {(invoiceData?.remaining_amount || 0) > 0 && (
                  <div className="my-4 rounded-xl border border-amber-200 bg-amber-50 p-4 print:hidden sm:my-6 sm:p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                      </div>
                      <div>
                        <p className="font-bold text-amber-800">Payment Required</p>
                        <p className="text-sm text-amber-700">Outstanding balance of <strong>PKR {invoiceData?.remaining_amount.toLocaleString()}</strong> is due. Please make payment to avoid service interruption.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Other Outstanding Invoices */}
                {invoiceData?.pending_invoices && invoiceData.pending_invoices.count > 0 && (
                  <div className="my-4 rounded-xl border border-red-200 bg-red-50 p-4 print:hidden sm:my-6 sm:p-5">
                    <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <p className="flex items-center gap-2 font-bold text-red-800">
                        Other Outstanding Invoices ({invoiceData.pending_invoices.count})
                      </p>
                      <span className="text-sm font-bold tabular-nums text-red-700">Total: PKR {invoiceData.pending_invoices.total_pending_amount.toLocaleString()}</span>
                    </div>
                    <div className="custom-scrollbar max-h-40 space-y-2 overflow-y-auto">
                      {invoiceData.pending_invoices.invoices.map((inv) => (
                        <div key={inv.id} className="flex flex-col gap-1 rounded-lg border border-red-100 bg-white px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
                            <span className="truncate font-mono font-semibold text-slate-700">#{inv.invoice_number}</span>
                            <span className="text-xs text-slate-400">{formatDate(inv.billing_start_date)}</span>
                          </div>
                          <span className="font-bold tabular-nums text-red-600">PKR {inv.remaining_amount.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bank Accounts */}
                {bankAccounts.length > 0 && (
                  <div className="py-8 border-b border-slate-100">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Payment Methods</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      {bankAccounts.map((account) => (
                        <InvoicePaymentMethodCard key={account.id} account={account} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {invoiceData?.notes && (
                  <div className="py-6">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Notes</p>
                    <p className="text-sm text-slate-600 bg-slate-50 rounded-xl p-4 border border-slate-100">{invoiceData.notes}</p>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-6 border-t border-slate-100 pt-6 text-center sm:mt-8 sm:pt-8">
                  <p className="mb-1 text-sm font-semibold text-slate-800">
                    {invoiceData?.company?.tagline || "Thank you for your business!"}
                  </p>
                  {invoiceData?.company?.invoice_footer_notes && (
                    <p className="text-xs text-slate-600 my-2 max-w-xl mx-auto whitespace-pre-line bg-slate-50 p-3 rounded-lg border border-slate-100">
                      {invoiceData.company.invoice_footer_notes}
                    </p>
                  )}
                  {(invoiceData?.company?.email || invoiceData?.company?.contact_number) && (
                    <p className="text-sm text-slate-500 mb-2">
                      Questions? {invoiceData.company.email && (
                        <a href={`mailto:${invoiceData.company.email}`} className="text-blue-600 hover:underline">{invoiceData.company.email}</a>
                      )}
                      {invoiceData.company.contact_number && (
                        <span>{invoiceData.company.email ? ' • ' : ''}{invoiceData.company.contact_number}</span>
                      )}
                    </p>
                  )}
                  <p className="text-xs text-slate-400">Invoice generated on {formatDate(new Date().toISOString())}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InvoiceGenerationPage
